import React, { useState, useEffect } from "react";
import { Modal, BlockStack, InlineStack, Text, Box, Icon, Badge } from "@shopify/polaris";
import { DragHandleMinor } from "@shopify/polaris-icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useMutation } from "@apollo/client";
import { useAppBridge } from "@shopify/app-bridge-react";
import { isEqual } from "lodash";
import { SAVE_ANNOUNCEMENT_SETTINGS } from "../../graphql/Mutations/Settings";
import { CampaignStatusEnum } from "../../pages/CampaignsPage/components/CampaignStatusEnum";

const UNTITLED = "Untitled campaign";

/**
 * Returns a copy of items with the item at index `from` moved to index `to`.
 */
export function moveItem(items, from, to) {
  const result = Array.from(items);
  const [moved] = result.splice(from, 1);
  result.splice(to, 0, moved);
  return result;
}

/**
 * The announcement text as shoppers read it, without any HTML the merchant put in it.
 */
const announcementText = (sale) =>
  [sale.TextLeft, sale.TextRight]
    .map((text) => (text || "").replace(/<[^>]*>/g, "").trim())
    .filter(Boolean)
    .join(" … ");

// Open-ended sales have no end date; it arrives as 0001-01-01 or the epoch
const hasDate = (date) => !!date && new Date(date).getTime() > 0;

const formatDay = (date) => new Date(date).toLocaleDateString(undefined, { day: "numeric", month: "short" });

const SaleRow = ({ sale }) => {
  const text = announcementText(sale);
  const scheduled = sale.Status === CampaignStatusEnum.Scheduled;
  const dateLabel = scheduled
    ? hasDate(sale.StartDate) && `Starts ${formatDay(sale.StartDate)}`
    : hasDate(sale.EndDate) && `Ends ${formatDay(sale.EndDate)}`;

  return (
    <InlineStack gap="300" blockAlign="center" wrap={false}>
      <div>
        <Icon source={DragHandleMinor} tone="subdued" />
      </div>
      <Box minWidth="0" width="100%">
        <BlockStack gap="050">
          <Text as="span" fontWeight="semibold" truncate>{text || sale.Title || UNTITLED}</Text>
          {text && <Text as="span" tone="subdued" truncate>{sale.Title || UNTITLED}</Text>}
        </BlockStack>
      </Box>
      {/* Keeps the badge and date on one line; the announcement text truncates instead */}
      <div style={{ flexShrink: 0 }}>
        <InlineStack gap="200" blockAlign="center" wrap={false}>
          {scheduled && <Badge tone="info">Scheduled</Badge>}
          {dateLabel && <Text as="span" tone="subdued">{dateLabel}</Text>}
        </InlineStack>
      </div>
    </InlineStack>
  );
};

/**
 * Lets the merchant drag their sales into the order the announcement bar rotates through them,
 * and saves that order on its own, apart from the bar's design.
 */
const AnnouncementSaleOrderModal = ({ appName, open, sales, onClose, onSaved }) => {
  const shopify = useAppBridge();
  const [order, setOrder] = useState(sales);
  const [saveOrder, { loading: saving }] = useMutation(SAVE_ANNOUNCEMENT_SETTINGS(appName));

  // Start from the saved order each time the modal opens
  useEffect(() => {
    if (open) setOrder(sales);
  }, [open, sales]);

  const handleDragEnd = ({ source, destination }) => {
    if (!destination || destination.index === source.index) return;
    setOrder(moveItem(order, source.index, destination.index));
  };

  const handleSave = async () => {
    try {
      await saveOrder({ variables: { input: { SaleOrder: order.map((sale) => sale.Id) } } });
      onSaved(order);
      onClose();
      shopify.toast.show("Sale order saved", { duration: 1500 });
    } catch (err) {
      console.error(err);
      shopify.toast.show(`Error saving sale order: ${err.message}`, { duration: 1500, isError: true });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sale order"
      primaryAction={{
        content: "Save",
        onAction: handleSave,
        disabled: isEqual(order, sales),
        loading: saving,
      }}
      secondaryActions={[{ content: "Cancel", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          <Text as="p" tone="subdued">
            Drag your sales into the order shoppers should see their announcements in the bar.
          </Text>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="announcement-sale-order">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {order.map((sale, index) => (
                    <Draggable key={sale.Id} draggableId={sale.Id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{ ...provided.draggableProps.style, cursor: "grab", paddingBottom: 8 }}
                          data-testid="sale-order-row"
                        >
                          <Box
                            padding="300"
                            borderWidth="025"
                            borderColor="border"
                            borderRadius="200"
                            background={snapshot.isDragging ? "bg-surface-hover" : "bg-surface"}
                            shadow={snapshot.isDragging ? "300" : undefined}
                          >
                            <SaleRow sale={sale} />
                          </Box>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default AnnouncementSaleOrderModal;
